// TagChooserFluent.js
// Custom chip-based tag chooser using Fluent UI v8 (no TagPicker)
// Shows two lists: Selected and Available. Click chips to add/remove.
// API it expects:
//  GET  /api/<entityType>/<entityId>/tags
//  POST /api/<entityType>/<entityId>/tags { tag_id }
//  DELETE /api/<entityType>/<entityId>/tags/:tagId
//  GET  /api/tags?q=<search>

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Stack,
  Label,
  Text,
  Spinner,
} from '@fluentui/react';
import { mergeStyles, mergeStyleSets } from '@fluentui/react';

export default function SkillsPicker({ personId, label = 'Skills', className, createMode=false }) {
  const [allTags, setAllTags] = useState([]);       // all available tags
  const [selected, setSelected] = useState([]);     // tags linked to this entity
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [query, setQuery] = useState('');

  // styles
  const styles = useMemo(() => mergeStyleSets({
    section: { borderRadius: 2, padding: 12 },
    headerRow: { marginBottom: 8 },
    chipRow: { display: 'flex', flexWrap: 'wrap', gap: 8 },
    chipBase: {
      display: 'inline-flex', alignItems: 'center', gap: 8,
      borderRadius: 9999, fontSize: 13, lineHeight: '28px', height: 28,
      padding: '0 10px', cursor: 'pointer', userSelect: 'none',
      border: '1px solid', transition: 'all .12s ease',
    },
    selectedChip: {
      background: 'var(--themePrimary, #0078d4)',
      color: 'white',
      borderColor: 'var(--themePrimary, #0078d4)'
    },
    availableChip: {
      background: 'transparent',
      color: 'inherit',
      borderColor: 'var(--neutralTertiaryAlt, #c8c8c8)'
    },
    chipIcon: { fontWeight: 700, opacity: 0.9 },
    subnote: { opacity: 0.7 },
  }), []);

  // Fetch selected tags for this entity
  useEffect(() => {
    if(createMode){
      setLoading(false);
      return;
    };
    let alive = true;
    const run = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/people/${personId}/skills`);
        const data = await res.json();
        if (alive) setSelected(data.sort((a,b)=>a.name.localeCompare(b.name)));
      } catch (e) {
        console.error(e);
      } finally {
        if (alive) setLoading(false);
      }
    };
    if (personId) run();
    return () => { alive = false; };
  }, [personId, createMode]);

  // Fetch all tags (with optional search)
  const fetchAllTags = useCallback(async (q) => {
    try {
      const res = await fetch(`/api/skills${q ? `?q=${encodeURIComponent(q)}` : ''}`);
      const data = await res.json();
      setAllTags(data);
      return data;
    } catch (e) {
      console.error(e);
      return [];
    }
  }, [createMode]);

  useEffect(() => { fetchAllTags(); }, [fetchAllTags]);

  // Partition into selected vs available (filtered by search)
  const selectedIds = useMemo(() => new Set(selected.map(t => t.id)), [selected]);
  const available = useMemo(() => {
    const filtered = allTags.filter(t => !selectedIds.has(t.id));
    if (!query) return filtered;
    const q = query.toLowerCase();
    return filtered.filter(t => t.name.toLowerCase().includes(q));
  }, [allTags, selectedIds, query]);

  // API mutations
  const addTag = useCallback(async (tagId) => {
    if(createMode){
      setSelected(prev => prev.concat(allTags.find(tag => tag.id === tagId)))
      return;
    }
    try {
      setAdding(true);
      const res = await fetch(`/api/people/${personId}/skills`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ skill_id: tagId  })
      });
      if (!res.ok) throw new Error('Add failed');
      const tag = await res.json();
      setSelected(prev => [...prev, tag].sort((a,b)=>a.name.localeCompare(b.name)));
    } catch (e) {
      console.error(e);
      alert('Failed to add tag');
    } finally { setAdding(false); }
  }, [personId, createMode, allTags]);

  const removeTag = useCallback(async (tagId) => {
    if(createMode){
      setSelected(prev => prev.filter(t => t.id !== tagId));
      return;
    }
    try {
      setRemoving(true);
      const res = await fetch(`/api/people/${personId}/skills/${tagId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Remove failed');
      setSelected(prev => prev.filter(t => t.id !== tagId));
    } catch (e) {
      console.error(e);
      alert('Failed to remove tag');
    } finally { setRemoving(false); }
  }, [personId, createMode, allTags]);

  // Chip element
  const Chip = ({ tag, selected }) => {
    const className = mergeStyles(
      styles.chipBase,
      selected ? styles.selectedChip : styles.availableChip,
      {
        selectors: {
          ':hover': selected ? { filter: 'brightness(0.95)' } : { background: 'rgba(0,0,0,0.03)' }
        }
      }
    );
    return (
      <button
        type="button"
        className={className}
        title={selected ? 'Remove tag' : 'Add tag'}
        onClick={() => (selected ? removeTag(tag.id) : addTag(tag.id))}
        aria-pressed={selected}
      >
        <span>{tag.name}</span>
        <span className={styles.chipIcon} aria-hidden>
          {selected ? '×' : '+'}
        </span>
      </button>
    );
  };

  return (
    <Stack tokens={{ childrenGap: 12 }} className={className}>
      <Stack horizontal verticalAlign="center" horizontalAlign="space-between" className={styles.headerRow}>
        <Text variant='xLarge'>{label}</Text>
        {(loading || adding || removing) && (
          <Stack horizontal tokens={{ childrenGap: 8 }} verticalAlign="center">
            {loading && <Spinner label="Loading" labelPosition="right" />}
            {adding && <Spinner label="Adding" labelPosition="right" />}
            {removing && <Spinner label="Removing" labelPosition="right" />}
          </Stack>
        )}
      </Stack>

      <div style={{display: 'flex', gap: 12}}>
        {/* Selected */}
        <div style={{flex: 1}} className={styles.section}>
          <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
            <Text variant="medium">Selected</Text>
            {/* <Text variant="small" className={styles.subnote}>{selected.length} chosen</Text> */}
          </Stack>
          <div className={styles.chipRow} style={{ marginTop: 8 }}>
            {selected.length === 0 ? (
              <Text variant="small" className={styles.subnote}>No tags selected</Text>
            ) : (
              selected.map(tag => <Chip key={tag.id} tag={tag} selected />)
            )}
          </div>
        </div>

        {/* Available */}
        <div style={{flex: 1}} className={styles.section}>
          <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
            <Text variant="medium">Available</Text>
            {/* <SearchBox
              placeholder="Filter available tags…"
              value={query}
              onChange={(_, v) => setQuery(v || '')}
              underlined
              styles={{ root: { maxWidth: 280 } }}
            /> */}
          </Stack>
          <div className={styles.chipRow} style={{ marginTop: 8 }}>
            {available.length === 0 ? (
              <Text variant="small" className={styles.subnote}>No available tags</Text>
            ) : (
              available.map(tag => <Chip key={tag.id} tag={tag} selected={false} />)
            )}
          </div>
        </div>
      </div>

      {/* <Text variant="small" className={styles.subnote}>
        Tip: Click a tag to {`add/remove`} it. Selected chips are colored; available chips are outlined.
      </Text> */}
    </Stack>
  );
}

